import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Upload,
  Users,
  Map,
  FileText,
  LogOut,
  User,
  Shield,
  Package,
  FileSpreadsheet,
  Truck,
  FileCheck,
  FileSignature,
  Building2,
  Code2,
  Receipt,
  ChevronDown,
  Settings,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const linkBase =
  "flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 border-transparent transition-colors whitespace-nowrap";
const linkInactive =
  "text-slate-300 hover:text-white hover:border-white/20";
const linkActive = "text-white border-[#D95A40]";

function TopLink({ to, testid, icon: Icon, label, end }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      end={end}
      className={({ isActive }) =>
        `${linkBase} ${isActive ? linkActive : linkInactive}`
      }
    >
      <Icon className="w-4 h-4" />
      {label}
    </NavLink>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      {/* Top navbar */}
      <header className="bg-[#0B1B3D] text-white sticky top-0 z-30 border-b border-white/10">
        <div className="max-w-[1600px] mx-auto flex items-center gap-2 px-4 md:px-6">
          {/* Logo */}
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2 py-3 pr-4 border-r border-white/10 shrink-0"
            data-testid="nav-logo"
          >
            <div className="w-9 h-9 bg-[#D95A40] flex items-center justify-center">
              <Package className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-base leading-none tracking-tighter">
                EXIS
              </div>
              <div className="font-display text-[9px] uppercase tracking-[0.3em] text-slate-300">
                Pharma
              </div>
            </div>
          </NavLink>

          {/* Nav */}
          <nav className="flex items-center gap-1 flex-1 overflow-x-auto">
            <TopLink
              to="/dashboard"
              testid="nav-dashboard"
              icon={LayoutDashboard}
              label="Dashboard"
              end
            />
            <TopLink
              to="/upload"
              testid="nav-upload"
              icon={Upload}
              label="Cargar XML"
            />
            <TopLink
              to="/batches"
              testid="nav-batches"
              icon={FileText}
              label={isAdmin ? "Todos los Envíos" : "Mis Productos"}
            />
            {!isAdmin && (
              <>
                <TopLink
                  to="/entregas"
                  testid="nav-client-rda"
                  icon={Truck}
                  label="Mis Entregas"
                />
                <TopLink
                  to="/cedibles"
                  testid="nav-client-cedibles"
                  icon={FileSignature}
                  label="Mis Cedibles"
                />
                <TopLink
                  to="/facturas"
                  testid="nav-client-facturas"
                  icon={Receipt}
                  label="Mis Facturas"
                />
              </>
            )}

            {isAdmin && (
              <>
                <TopLink
                  to="/admin/clientes"
                  testid="nav-admin-clientes"
                  icon={Building2}
                  label="Clientes"
                />
                <TopLink
                  to="/admin/rda"
                  testid="nav-admin-rda"
                  icon={Truck}
                  label="RDA"
                />
                <TopLink
                  to="/admin/consolidado"
                  testid="nav-admin-consolidado"
                  icon={FileCheck}
                  label="Consolidado"
                />
                <TopLink
                  to="/admin/cedible"
                  testid="nav-admin-cedible"
                  icon={FileSignature}
                  label="CEDIBLE"
                />
                <TopLink
                  to="/admin/facturas"
                  testid="nav-admin-facturas"
                  icon={Receipt}
                  label="Facturas"
                />

                {/* More admin options */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-admin-more"
                      className={`${linkBase} ${linkInactive} focus:outline-none`}
                    >
                      <Settings className="w-4 h-4" />
                      Más
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="bg-white text-[#0B1B3D] min-w-[220px]"
                    align="end"
                  >
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Administración
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/sap")}
                      data-testid="nav-admin-sap"
                      className="gap-2 cursor-pointer"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      SAP por Cliente
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/documents")}
                      data-testid="nav-admin-documents"
                      className="gap-2 cursor-pointer"
                    >
                      <FileText className="w-4 h-4" />
                      Todos los Documentos
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/users")}
                      data-testid="nav-admin-users"
                      className="gap-2 cursor-pointer"
                    >
                      <Users className="w-4 h-4" />
                      Usuarios
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/comunas")}
                      data-testid="nav-admin-comunas"
                      className="gap-2 cursor-pointer"
                    >
                      <Map className="w-4 h-4" />
                      Comunas & Regiones
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate("/api-docs")}
                      data-testid="nav-api"
                      className="gap-2 cursor-pointer"
                    >
                      <Code2 className="w-4 h-4" />
                      API
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            {!isAdmin && (
              <TopLink
                to="/api-docs"
                testid="nav-api"
                icon={Code2}
                label="API"
              />
            )}
          </nav>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 shrink-0"
              >
                <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
                  {isAdmin ? (
                    <Shield className="w-4 h-4 text-[#D95A40]" />
                  ) : (
                    <User className="w-4 h-4 text-slate-300" />
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-xs font-semibold leading-tight max-w-[140px] truncate">
                    {user?.name}
                  </div>
                  <div className="text-[9px] text-slate-400 uppercase tracking-wider">
                    {user?.role}
                  </div>
                </div>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="bg-white text-[#0B1B3D] min-w-[200px]"
              align="end"
            >
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="logout-btn"
                className="gap-2 cursor-pointer text-red-600 focus:text-red-700"
              >
                <LogOut className="w-4 h-4" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
